import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Landing    from './pages/Landing';
import Pricing    from './pages/Pricing';
import Login      from './pages/Login';
import Register   from './pages/Register';
import CompleteProfile from './pages/CompleteProfile';
import AdminLogin from './pages/AdminLogin';
import Dashboard  from './pages/Dashboard';
import Challenge  from './pages/Challenge';
import Bots       from './pages/Bots';
import SimTrade      from './pages/SimTrade';
import CryptoCheckout from './pages/CryptoCheckout';
import PaymentSuccess from './pages/PaymentSuccess';
import AdminPanel from './pages/AdminPanel';

import './index.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Protected route wrapper
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading...</div>;
  return user ? children : <Navigate to="/login" replace />;
}

// Admin route wrapper
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

// Conditionally wrap with Google provider only when client ID exists
function GoogleWrapper({ children }) {
  if (GOOGLE_CLIENT_ID) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{children}</GoogleOAuthProvider>;
  }
  return children;
}

function App() {
  return (
    <GoogleWrapper>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#0c1120',
                color: '#f0f4ff',
                border: '1px solid rgba(255,255,255,0.1)',
                fontFamily: 'Syne, sans-serif',
                fontSize: '14px',
              },
              success: { iconTheme: { primary: '#10e88a', secondary: '#050810' } },
              error:   { iconTheme: { primary: '#ff4d6d', secondary: '#050810' } },
            }}
          />
          <Routes>
            {/* Public */}
            <Route path="/"               element={<Landing />} />
            <Route path="/pricing"        element={<Pricing />} />
            <Route path="/login"          element={<Login />} />
            <Route path="/register"       element={<Register />} />
            <Route path="/admin/login"    element={<AdminLogin />} />

            {/* Profile completion */}
            <Route path="/complete-profile" element={<PrivateRoute><CompleteProfile /></PrivateRoute>} />

            {/* Protected */}
            <Route path="/dashboard"      element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/bots"           element={<PrivateRoute><Bots /></PrivateRoute>} />
            <Route path="/sim-trade"       element={<PrivateRoute><SimTrade /></PrivateRoute>} />
            <Route path="/crypto-checkout" element={<PrivateRoute><CryptoCheckout /></PrivateRoute>} />
            <Route path="/challenge/:id"  element={<PrivateRoute><Challenge /></PrivateRoute>} />
            <Route path="/payment/success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />

            {/* Admin */}
            <Route path="/admin"          element={<AdminRoute><AdminPanel /></AdminRoute>} />

            {/* Fallback */}
            <Route path="*"               element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </GoogleWrapper>
  );
}

export default App;
